/** Minimal math helpers. Column-major mat4, matching WGSL's mat4x4<f32> memory layout. */

export type Mat4 = Float32Array<ArrayBuffer>;
export type Vec3 = { x: number; y: number; z: number };

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp01((x - e0) / (e1 - e0 || 1e-6));
  return t * t * (3 - 2 * t);
};
export const sign = Math.sign;
export const TAU = Math.PI * 2;

/**
 * Planar length. `Math.hypot` is the obvious spelling and costs 22.97 ns
 * against 2.49 ns for this — 9.2x, measured over 20M calls — because it is
 * variadic and guards against intermediate overflow. Neither matters here:
 * world coordinates stay under 1e5, so `a*a + b*b` cannot come near the range
 * where the guard earns its keep. This is on the per-frame path in the beam
 * march, the shoal and every enemy, so the difference is not academic.
 */
export const hyp = (dx: number, dy: number) => Math.sqrt(dx * dx + dy * dy);

/** Frame-rate independent exponential approach. `rate` = how much of the gap is closed per second. */
export function damp(current: number, target: number, rate: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-rate * dt));
}

/** Shortest signed angular difference from a to b. */
export function angleDelta(a: number, b: number): number {
  let d = (b - a) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return d;
}

export function dampAngle(current: number, target: number, rate: number, dt: number): number {
  return current + angleDelta(current, target) * (1 - Math.exp(-rate * dt));
}

export function mat4(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

export function identity(out: Mat4): Mat4 {
  out.fill(0);
  out[0] = out[5] = out[10] = out[15] = 1;
  return out;
}

export function multiply(out: Mat4, a: Mat4, b: Mat4): Mat4 {
  const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
  const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
  const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
  const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];
  for (let i = 0; i < 4; i++) {
    const b0 = b[i * 4], b1 = b[i * 4 + 1], b2 = b[i * 4 + 2], b3 = b[i * 4 + 3];
    out[i * 4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[i * 4 + 1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[i * 4 + 2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[i * 4 + 3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
  }
  return out;
}

/** Right-handed perspective with WebGPU's [0,1] depth range. */
/**
 * Orthographic projection looking straight DOWN, for the height map.
 *
 * Built here rather than composed from a look-at and an ortho because the
 * camera never rotates: world x maps to clip x, world z maps to clip y, and
 * world y becomes depth. Three scales and three offsets, and no matrix
 * multiply — which also means there is no basis to get the handedness of.
 *
 * `top` is the highest y the map can record and maps to depth 0; `bottom` maps
 * to depth 1. Anything above `top` is clipped away, which is what keeps the
 * water surface out of a map that is meant to hold terrain.
 */
export function orthoTopDown(
  out: Mat4,
  cx: number, cz: number, halfX: number, halfZ: number,
  top: number, bottom: number,
): Mat4 {
  const sy = 1 / Math.max(top - bottom, 1e-3);
  out.fill(0);
  out[0] = 1 / halfX;
  out[9] = -1 / halfZ;          // world z -> clip y, negated so +z is up-screen
  out[6] = -sy;                 // world y -> depth, 0 at `top`
  out[12] = -cx / halfX;
  out[13] = cz / halfZ;
  out[14] = top * sy;
  out[15] = 1;
  return out;
}

export function perspective(out: Mat4, fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[11] = -1;
  out[10] = far / (near - far);
  out[14] = (far * near) / (near - far);
  return out;
}

/** Right-handed look-at (camera looks down -Z). */
export function lookAt(out: Mat4, eye: Vec3, target: Vec3, upY = 1): Mat4 {
  let zx = eye.x - target.x, zy = eye.y - target.y, zz = eye.z - target.z;
  let len = Math.hypot(zx, zy, zz) || 1;
  zx /= len; zy /= len; zz /= len;
  // up = (0, upY, 0)
  let xx = upY * zz - 0 * zy;
  let xy = 0 * zx - 0 * zz;
  let xz = 0 * zy - upY * zx;
  len = Math.hypot(xx, xy, xz) || 1;
  xx /= len; xy /= len; xz /= len;
  const yx = zy * xz - zz * xy;
  const yy = zz * xx - zx * xz;
  const yz = zx * xy - zy * xx;
  out[0] = xx; out[1] = yx; out[2] = zx; out[3] = 0;
  out[4] = xy; out[5] = yy; out[6] = zy; out[7] = 0;
  out[8] = xz; out[9] = yz; out[10] = zz; out[11] = 0;
  out[12] = -(xx * eye.x + xy * eye.y + xz * eye.z);
  out[13] = -(yx * eye.x + yy * eye.y + yz * eye.z);
  out[14] = -(zx * eye.x + zy * eye.y + zz * eye.z);
  out[15] = 1;
  return out;
}

export function invert(out: Mat4, m: Mat4): Mat4 {
  const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
  const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
  const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
  const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;
  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (!det) return identity(out);
  det = 1 / det;
  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return out;
}

/** Compose translation * rotZ * rotY * rotX * scale into `out`. */
/**
 * Rotate a mesh-local offset by the same R = Rz·Ry·Rx that `compose` builds.
 *
 * This is how anything BOLTED TO a drawn mesh finds its world position — the
 * propeller, the searchlight, the status lamps. Deriving those from an aim
 * angle instead looks identical while the boat flies straight and comes apart
 * the moment it turns, because a heading flips the instant the input does while
 * the drawn hull swings round over the following sixth of a second: for those
 * few frames the fittings are placed against a hull orientation that is not on
 * screen, and the engine visibly leaves the axis.
 *
 * Taking the rotation itself removes the question. There is no second angle to
 * keep in step, so there is nothing to fall out of step.
 */
export function rotateOffset(
  out: { x: number; y: number; z: number },
  rx: number, ry: number, rz: number,
  ox: number, oy: number, oz: number,
): { x: number; y: number; z: number } {
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const cy = Math.cos(ry), sy = Math.sin(ry);
  const cz = Math.cos(rz), sz = Math.sin(rz);
  out.x = (cz * cy) * ox + (cz * sy * sx - sz * cx) * oy + (cz * sy * cx + sz * sx) * oz;
  out.y = (sz * cy) * ox + (sz * sy * sx + cz * cx) * oy + (sz * sy * cx - cz * sx) * oz;
  out.z = (-sy) * ox + (cy * sx) * oy + (cy * cx) * oz;
  return out;
}

export function compose(
  out: Mat4,
  px: number, py: number, pz: number,
  rx: number, ry: number, rz: number,
  sx: number, sy = sx, sz = sx,
): Mat4 {
  const cx = Math.cos(rx), sxr = Math.sin(rx);
  const cy = Math.cos(ry), syr = Math.sin(ry);
  const cz = Math.cos(rz), szr = Math.sin(rz);
  // R = Rz * Ry * Rx
  const m00 = cz * cy;
  const m01 = cz * syr * sxr - szr * cx;
  const m02 = cz * syr * cx + szr * sxr;
  const m10 = szr * cy;
  const m11 = szr * syr * sxr + cz * cx;
  const m12 = szr * syr * cx - cz * sxr;
  const m20 = -syr;
  const m21 = cy * sxr;
  const m22 = cy * cx;
  out[0] = m00 * sx; out[1] = m10 * sx; out[2] = m20 * sx; out[3] = 0;
  out[4] = m01 * sy; out[5] = m11 * sy; out[6] = m21 * sy; out[7] = 0;
  out[8] = m02 * sz; out[9] = m12 * sz; out[10] = m22 * sz; out[11] = 0;
  out[12] = px; out[13] = py; out[14] = pz; out[15] = 1;
  return out;
}

/**
 * A view-fraction rect as integer pixels of a target, clamped inside it.
 *
 * CLAMPED BECAUSE THE ALTERNATIVE IS A VALIDATION ERROR, NOT A WRONG PICTURE.
 * `setScissorRect` requires the rect to lie within the attachment, and the
 * panel's rect comes from a HUD layout that is free to put a viewport hard
 * against a screen edge — one pixel of rounding past it and the whole frame is
 * rejected. That is a class of bug no screenshot can find and no type can
 * catch, so it is a pure function with a check of its own.
 *
 * Rounded outward, so a half-covered edge pixel is included rather than
 * cropped: the panel is drawn with an antialiased frame, and clipping the
 * bloom a pixel inside it would leave a dark line where the glow stops.
 */
export function maskRect(
  r: { u: number; v: number; wf: number; hf: number }, w: number, h: number,
): [number, number, number, number] {
  const x0 = Math.max(0, Math.min(w - 1, Math.floor((r.u - r.wf / 2) * w)));
  const y0 = Math.max(0, Math.min(h - 1, Math.floor((r.v - r.hf / 2) * h)));
  const x1 = Math.max(x0 + 1, Math.min(w, Math.ceil((r.u + r.wf / 2) * w)));
  const y1 = Math.max(y0 + 1, Math.min(h, Math.ceil((r.v + r.hf / 2) * h)));
  return [x0, y0, x1 - x0, y1 - y0];
}
